/**
 * Offers Redux Slice
 * 
 * Clean state management for offers list with filtering and pagination.
 * Follows single responsibility - only manages offer list state.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import type {
  OfferResponse,
  OfferQueryParams,
} from '@/app/types/offers';
import { offerService } from '@/app/services/offerService';

// ============================================================================
// Types
// ============================================================================

interface OfferListResponse {
  offers: OfferResponse[];
  total: number;
}

interface OffersState {
  items: OfferResponse[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  filters: OfferQueryParams;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  invalidated: boolean;
}

// ============================================================================
// Constants
// ============================================================================

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export type PageSizeOption = typeof PAGE_SIZE_OPTIONS[number];

const DEFAULT_PAGE_SIZE: PageSizeOption = 10;

// ============================================================================
// Initial State
// ============================================================================

const initialFilters: OfferQueryParams = {
  group_by: 'user_id',  // Always group by user for proper pagination
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
};

const initialState: OffersState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  filters: initialFilters,
  status: 'idle',
  error: null,
  invalidated: false,
};

// ============================================================================
// Async Thunks
// ============================================================================

export const fetchOffers = createAsyncThunk<
  OfferListResponse,
  OfferQueryParams | undefined,
  { state: RootState; rejectValue: string }
>(
  'offers/fetch',
  async (params, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const filters = params ?? state.offers.filters;
      return await offerService.queryOffers(filters);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch offers'
      );
    }
  }
);

// ============================================================================
// Slice
// ============================================================================

const offersSlice = createSlice({
  name: 'offers',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<OfferQueryParams>>) => {
      // Merge new filters with existing, reset to page 1
      state.filters = {
        ...state.filters,
        ...action.payload,
        offset: 0, // Reset pagination when filters change
      };
      state.currentPage = 1;
    },
    
    setPage: (state, action: PayloadAction<number>) => {
      const page = action.payload;
      state.currentPage = page;
      state.filters.offset = (page - 1) * state.pageSize;
    },
    
    setPageSize: (state, action: PayloadAction<PageSizeOption>) => {
      const newSize = action.payload;
      state.pageSize = newSize;
      state.filters.limit = newSize;
      state.filters.offset = 0;
      state.currentPage = 1;
    },
    
    clearFilters: (state) => {
      state.filters = { ...initialFilters, limit: state.pageSize };
      state.currentPage = 1;
    },
    
    invalidate: (state) => {
      state.invalidated = true;
    },
    
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOffers.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchOffers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.offers;
        state.totalCount = action.payload.total;
        state.invalidated = false;
      })
      .addCase(fetchOffers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'Unknown error';
      });
  },
});

// ============================================================================
// Actions & Selectors
// ============================================================================

export const { setFilters, setPage, setPageSize, clearFilters, invalidate, resetState } = offersSlice.actions;

// Selectors
export const selectOffers = (state: RootState) => state.offers.items;
export const selectOffersTotalCount = (state: RootState) => state.offers.totalCount;
export const selectOffersCurrentPage = (state: RootState) => state.offers.currentPage;
export const selectOffersPageSize = (state: RootState) => state.offers.pageSize;
export const selectOffersFilters = (state: RootState) => state.offers.filters;
export const selectOffersStatus = (state: RootState) => state.offers.status;
export const selectOffersError = (state: RootState) => state.offers.error;

// Derived selectors
export const selectOffersIsLoading = (state: RootState) => 
  state.offers.status === 'loading';

export const selectOffersInvalidated = (state: RootState) => 
  state.offers.invalidated;

export const selectOffersTotalPages = (state: RootState) => 
  Math.ceil(state.offers.totalCount / state.offers.pageSize);

export default offersSlice.reducer;


